import {
  HealthInvestigation,
  hasAITrace,
  AsyncOperationEvent,
  useSiteHealthInvestigationSteps,
  useSiteHealthInvestigationTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { formatSiteDateTime } from "../healthDateFilters";
import { AgentStepFeed, AgentStepRecord } from "./AgentStepFeed";
import InvestigationMarkdown from "./InvestigationMarkdown";
import InvestigationStatusBadge from "./InvestigationStatusBadge";
import {
  formatInvestigationCost,
  GENERAL_INVESTIGATION_LABEL,
  investigationStatusMessage,
  InvestigationSession,
} from "./investigationSessions";

const MAX_QUESTION_LENGTH = 2000;
// Operators type multi-sentence questions, so the box starts several lines tall
// and grows with the text. The cap is viewport-relative as well as absolute:
// 260px is a comfortable desktop ceiling, but on a phone it is most of the
// screen, and a composer that tall leaves no room for the thread above it or
// the send button below.
const QUESTION_ROWS = 5;
const QUESTION_MAX_HEIGHT = "min(260px, 30vh)";

// Same scoped markdown styling as the demand coach (DemandFileProcessModal.tsx)
// so AI-written markdown renders legibly without blowing up heading sizes or
// list indents. Injected once per modal, not once per answer.
const MARKDOWN_STYLE = `
  .ai-analysis-markdown h1,
  .ai-analysis-markdown h2,
  .ai-analysis-markdown h3 { font-size: 1.05em; font-weight: 600; margin: 0.6em 0 0.2em; }
  .ai-analysis-markdown p { margin: 0 0 0.6em; }
  .ai-analysis-markdown ul, .ai-analysis-markdown ol { margin: 0 0 0.6em; padding-left: 1.5em; }
  .ai-analysis-markdown code { background: rgba(0,0,0,0.06); padding: 0 0.25em; border-radius: 3px; }
  .ai-analysis-markdown pre { background: rgba(0,0,0,0.06); padding: 0.5em; border-radius: 4px; overflow-x: auto; }
  .ai-analysis-markdown pre code { background: transparent; padding: 0; }
  .ai-analysis-markdown table { width: auto; margin: 0 0 0.6em; border-collapse: collapse; }
  .ai-analysis-markdown th, .ai-analysis-markdown td { border: 1px solid rgba(0,0,0,0.12); padding: 0.2em 0.5em; }
`;

// Collapsed-by-default AI trace disclosure. The trace is only fetched once the
// operator opens the <details> (the useLandlordResearchTrace lazy pattern) and
// rendered via the shared AITraceViewer, with loading / no-trace branches
// (traces aren't copied into non-prod envs, where the fetch 404s).
const InvestigationStepsDisclosure = ({
  siteId,
  runId,
}: {
  siteId: string;
  runId: string | null;
}) => {
  const [open, setOpen] = useState(false);
  const stepsQuery = useSiteHealthInvestigationSteps(siteId, runId, open);
  return (
    <AgentStepRecord
      steps={stepsQuery.data}
      isLoading={open && stepsQuery.isLoading}
      onOpen={setOpen}
    />
  );
};

const InvestigationTraceDisclosure = ({
  siteId,
  runId,
}: {
  siteId: string;
  runId: string | null;
}) => {
  const [showTrace, setShowTrace] = useState(false);
  const traceQuery = useSiteHealthInvestigationTrace(siteId, runId, showTrace);
  return (
    <details
      className="mt-3"
      onToggle={(e) => setShowTrace((e.target as HTMLDetailsElement).open)}
    >
      <summary
        style={{ cursor: "pointer", fontSize: "0.85em" }}
        className="text-muted"
      >
        Show full AI trace
      </summary>
      <div className="mt-2">
        {showTrace && traceQuery.isLoading ? (
          <div className="text-muted small">Loading trace...</div>
        ) : hasAITrace(traceQuery.data) ? (
          <AITraceViewer trace={traceQuery.data} />
        ) : (
          <div className="text-muted small">
            No AI trace available for this run.
          </div>
        )}
      </div>
    </details>
  );
};

const QuestionBlock = ({ question }: { question: string | null }) => (
  <div
    className="p-2 rounded"
    style={{ background: "rgba(0,0,0,0.04)", borderLeft: "3px solid #adb5bd" }}
  >
    {question ? (
      <span style={{ whiteSpace: "pre-wrap" }}>{question}</span>
    ) : (
      <span className="text-muted fst-italic">
        {GENERAL_INVESTIGATION_LABEL}
      </span>
    )}
  </div>
);

const InvestigationAnswer = ({
  run,
  siteId,
  timeZone,
}: {
  run: HealthInvestigation;
  siteId: string;
  timeZone: string;
}) => {
  const statusMessage = investigationStatusMessage(run.status);
  const cost = formatInvestigationCost(run.costUsd);
  return (
    <div className="mt-2">
      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
        <InvestigationStatusBadge status={run.status} />
        <span className="text-muted small">
          {formatSiteDateTime(run.createdAt, timeZone)}
          {run.createdBy ? ` by ${run.createdBy}` : ""}
          {run.modelId ? ` - ${run.modelId}` : ""}
          {cost ? ` - ${cost}` : ""}
        </span>
      </div>
      {statusMessage && <Alert variant="warning">{statusMessage}</Alert>}
      {run.reportMarkdown ? (
        <div className="ai-analysis-markdown" style={{ fontSize: "0.95em" }}>
          <InvestigationMarkdown>{run.reportMarkdown}</InvestigationMarkdown>
        </div>
      ) : (
        <div className="text-muted">No report was produced for this run.</div>
      )}
      {/* Steps first: the readable account of what the run did. The full trace
          below is the same interaction at debugging resolution. */}
      <InvestigationStepsDisclosure
        siteId={siteId}
        runId={run.traceRunId ?? run.runId}
      />
      <InvestigationTraceDisclosure
        siteId={siteId}
        runId={run.traceRunId ?? run.runId}
      />
    </div>
  );
};

// The investigation thread: the operator asks, the agent answers, and the same
// modal stays open across follow-ups. `session` is null for a session that
// hasn't been started yet.
const InvestigationThreadModal = ({
  siteId,
  siteName,
  timeZone,
  session,
  pendingQuestion,
  isPending,
  isOtherRunPending,
  progressMessage,
  events,
  errorMessage,
  startErrorMessage,
  noticeMessage,
  isSending,
  onSend,
  onBack,
  onHide,
}: {
  siteId: string;
  siteName: string;
  timeZone: string;
  session: InvestigationSession | null;
  /** The in-flight turn's question, held on screen until its answer lands. */
  pendingQuestion: string | null;
  isPending: boolean;
  /** A run is in flight for this site but on a different thread - the backend
   * keeps one investigation per site, so sending here would be rejected. */
  isOtherRunPending: boolean;
  progressMessage: string | null;
  events: AsyncOperationEvent[] | undefined;
  errorMessage: string | null;
  startErrorMessage: string | null;
  noticeMessage: string | null;
  isSending: boolean;
  /** Resolves true when the question was accepted, so the input can clear. */
  onSend: (question: string) => Promise<boolean>;
  /** Switches this modal surface to the all-investigations list. */
  onBack: () => void;
  onHide: () => void;
}) => {
  const [question, setQuestion] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const questionRef = useRef<HTMLTextAreaElement | null>(null);
  // Latches the send between the keypress and `isSending` arriving as a prop -
  // key-repeat on the shortcut fires many keydowns inside that window.
  const sendInFlightRef = useRef(false);
  const runs = session?.runs ?? [];
  const isFirstTurn = runs.length === 0 && !isPending;

  // Keep the newest turn in view as the thread grows and as progress ticks.
  const runCount = runs.length;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [runCount, isPending, progressMessage]);

  // Grow the input with the question instead of scrolling it: clearing the
  // inline height returns it to the rows={QUESTION_ROWS} floor, and it only
  // grows from there once the text overflows. The ceiling is the CSS
  // max-height, so it stays one rule that also tracks the viewport - past it
  // the box scrolls internally rather than growing.
  useEffect(() => {
    const el = questionRef.current;
    if (!el) return;
    el.style.height = "";
    if (el.scrollHeight > el.clientHeight) {
      const borders = el.offsetHeight - el.clientHeight;
      el.style.height = `${el.scrollHeight + borders}px`;
    }
  }, [question]);

  const sendDisabled =
    isSending ||
    isPending ||
    isOtherRunPending ||
    (!isFirstTurn && question.trim().length === 0);

  // The single send path, shared by the Send button and the Cmd/Ctrl+Enter
  // shortcut, so the shortcut can't bypass a guard the button honours.
  const handleSend = async () => {
    if (sendDisabled || sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    try {
      const accepted = await onSend(question.trim());
      if (accepted) setQuestion("");
    } finally {
      sendInFlightRef.current = false;
    }
  };

  return (
    <Modal
      show
      onHide={onHide}
      size="xl"
      dialogClassName="modal-90w modal-thread"
      scrollable
    >
      <Modal.Header closeButton>
        {/* Titled from the site, never from the question asked, so the title is
            predictable and doesn't change under the operator mid-thread. */}
        <Modal.Title>
          {siteName} - {session ? "AI investigation" : "New AI investigation"}
        </Modal.Title>
      </Modal.Header>
      {/* No min-height here: it would be honoured at the footer's expense, and
          the footer is where the composer lives. .modal-thread (index.css)
          floors the whole content instead, leaving the body free to shrink. */}
      <Modal.Body>
        <style>{MARKDOWN_STYLE}</style>
        {isFirstTurn && (
          <p className="text-muted small">
            The AI agent investigates this site&apos;s current health using
            health events, raw and processed readings, ping checks and
            expected-generation data. A run takes a few minutes. Ask a specific
            question, or leave it blank for a general health investigation.
          </p>
        )}
        {runs.map((run) => (
          <div key={run.runId} className="mb-4">
            <QuestionBlock question={run.question} />
            <InvestigationAnswer
              run={run}
              siteId={siteId}
              timeZone={timeZone}
            />
          </div>
        ))}
        {isPending && (
          <div className="mb-4">
            {/* No question block when we don't know what was asked - a coalesced
                kickoff follows someone else's run. */}
            {pendingQuestion !== null && (
              <QuestionBlock question={pendingQuestion} />
            )}
            <div className="d-flex align-items-center gap-2 text-muted small mt-2">
              <Spinner animation="border" size="sm" />
              <span>{progressMessage ?? "Investigating..."}</span>
            </div>
            {/* Expanded while the turn runs - this is the only moment anyone
                watches it. It collapses into the step record once the answer
                lands, below. */}
            <AgentStepFeed events={events} />
          </div>
        )}
        {noticeMessage && (
          <Alert variant="info" className="mb-2">
            {noticeMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert variant="danger" className="mb-2">
            {errorMessage}
          </Alert>
        )}
        {startErrorMessage && (
          <Alert variant="danger" className="mb-2">
            {startErrorMessage}
          </Alert>
        )}
        <div ref={bottomRef} />
      </Modal.Body>
      <Modal.Footer className="d-block">
        <Form.Group>
          <Form.Control
            as="textarea"
            ref={questionRef}
            rows={QUESTION_ROWS}
            style={{ maxHeight: QUESTION_MAX_HEIGHT }}
            value={question}
            maxLength={MAX_QUESTION_LENGTH}
            disabled={isPending}
            placeholder={
              isFirstTurn
                ? "e.g. why did generation drop on Tuesday? (optional)"
                : "Ask a follow-up question"
            }
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              // Plain Enter stays a newline - these are multi-sentence
              // questions - so only the modified Enter sends.
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
        </Form.Group>
        {/* Keyboard-only advice, and a line of footer height that a phone can't
            spare. */}
        <div className="text-muted small mt-1 d-none d-sm-block">
          ⌘Enter / Ctrl+Enter to send
        </div>
        {isOtherRunPending && (
          <div className="text-muted small mt-1">
            Another investigation is running for this site - wait for it to
            finish before sending.
          </div>
        )}
        {/* In the footer so it survives the body scrolling: every thread - a
            brand-new one included - can always reach the list. */}
        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
          <Button variant="link" className="p-0" onClick={onBack}>
            &lt; Back to all investigations
          </Button>
          {/* ms-auto rather than justify-content-between so the pair stays
              right-aligned on a narrow screen too, where it wraps onto its own
              line below the back link. */}
          <div className="d-flex gap-2 ms-auto">
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
            <Button
              variant="primary"
              disabled={sendDisabled}
              onClick={handleSend}
            >
              {isSending ? (
                <Spinner animation="border" size="sm" />
              ) : isFirstTurn ? (
                "Start investigation"
              ) : (
                "Send"
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InvestigationThreadModal;
